/**
 * Page de gestion des connexions Email Agent
 * Design cohérent avec KnowledgesPage et AgentsPage
 */
import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { 
  Mail, Plus, Settings, Trash2, RefreshCw, 
  CheckCircle, XCircle, Clock, AlertTriangle,
  Filter, MailCheck, Loader2, Info
} from 'lucide-react'
import { useEmailConnections, useDeleteEmailConnection, useUpdateEmailConnection, useEmailStats } from '../hooks/useEmailConnections'
import { EmailConnectionEditorModal } from '../components/email/EmailConnectionEditorModal'
import { EmailPatternsModal } from '../components/email/EmailPatternsModal'
import { EmailMessagesModal } from '../components/email/EmailMessagesModal'
import type { EmailConnection } from '../types/email'
import { useAppLanguage } from '../i18n'

const providerLabels: Record<string, string> = {
  gmail: 'Gmail',
  outlook: 'Outlook',
  imap: 'IMAP',
}

export default function EmailConnectionsPage() {
  const { t, language } = useAppLanguage()
  const [searchParams, setSearchParams] = useSearchParams()
  const { data: connections, isLoading, refetch } = useEmailConnections()
  const deleteConnection = useDeleteEmailConnection()
  const updateConnection = useUpdateEmailConnection()
  
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [editingConnection, setEditingConnection] = useState<EmailConnection | null>(null)
  const [patternsConnection, setPatternsConnection] = useState<EmailConnection | null>(null)
  const [messagesConnection, setMessagesConnection] = useState<EmailConnection | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<EmailConnection | null>(null)
  const [deletePending, setDeletePending] = useState(false)
  const [preselectedAgentId, setPreselectedAgentId] = useState<number | null>(null)

  // Gérer le paramètre assign_agent depuis AgentsPage
  useEffect(() => {
    const agentId = searchParams.get('assign_agent')
    if (agentId) {
      setPreselectedAgentId(parseInt(agentId))
      setShowCreateModal(true)
      // Nettoyer le paramètre après utilisation
      searchParams.delete('assign_agent')
      setSearchParams(searchParams, { replace: true })
    }
  }, [searchParams, setSearchParams])

  const handleToggleEnabled = async (connection: EmailConnection) => {
    await updateConnection.mutateAsync({
      id: connection.id,
      enabled: !connection.enabled,
    })
  }

  const handleToggleAutoReply = async (connection: EmailConnection) => {
    await updateConnection.mutateAsync({
      id: connection.id,
      auto_reply_enabled: !connection.auto_reply_enabled,
    })
  }

  const confirmDelete = async () => {
    if (!deleteTarget) return
    setDeletePending(true)
    try {
      await deleteConnection.mutateAsync(deleteTarget.id)
      setDeleteTarget(null)
    } finally {
      setDeletePending(false)
    }
  }

  return (
    <div className="relative overflow-hidden min-h-screen">
      {/* Background - Same as KnowledgesPage */}
      <div aria-hidden className="absolute inset-0 -z-30 bg-gradient-to-b from-emerald-50 via-white to-white dark:from-zinc-950 dark:via-zinc-900 dark:to-zinc-900" />
      <div aria-hidden className="absolute inset-x-0 top-0 -z-40 h-[360px] bg-[radial-gradient(circle_at_top,_rgba(16,185,129,0.25),_transparent_65%)]" />
      <div aria-hidden className="absolute inset-0 -z-20 bg-[radial-gradient(circle_at_25%_18%,rgba(236,72,153,0.14),transparent_60%),radial-gradient(circle_at_80%_12%,rgba(168,85,247,0.18),transparent_55%)] dark:bg-[radial-gradient(circle_at_20%_18%,rgba(236,72,153,0.12),transparent_55%),radial-gradient(circle_at_85%_18%,rgba(168,85,247,0.15),transparent_50%)]" />

      <div className="relative mx-auto max-w-[1600px] px-6 py-6">
        {/* Barre d'actions - Same style as KnowledgesPage */}
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div className="max-w-3xl space-y-1">
            <p className="text-sm text-zinc-500 dark:text-zinc-400">{t.email.tagline}</p>
            {connections && connections.length > 0 && (
              <div className="mt-3 flex gap-3 rounded-2xl border border-emerald-200/60 bg-emerald-50/50 px-4 py-3 dark:border-emerald-900/50 dark:bg-emerald-950/25">
                <Info className="h-5 w-5 shrink-0 text-emerald-600 dark:text-emerald-400" aria-hidden />
                <div className="min-w-0 text-sm">
                  <p className="font-semibold text-emerald-900 dark:text-emerald-200">{t.email.observeTitle}</p>
                  <p className="mt-1 leading-relaxed text-zinc-600 dark:text-zinc-300">{t.email.observeBody}</p>
                </div>
              </div>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => refetch()}
              disabled={isLoading}
              className="inline-flex items-center gap-2 rounded-full border border-zinc-200/70 bg-white/80 px-4 py-2 text-sm font-semibold text-zinc-600 shadow-sm transition hover:bg-zinc-100 dark:border-zinc-700 dark:bg-zinc-900/80 dark:text-zinc-200 dark:hover:bg-zinc-800"
            >
              <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">{t.email.refresh}</span>
            </button>
            <button
              type="button"
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center gap-2 rounded-full border border-emerald-200/70 bg-emerald-500 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-600 dark:border-emerald-700"
            >
              <Plus className="h-4 w-4" />
              <span>{t.email.newConnection}</span>
            </button>
          </div>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
          </div>
        )}

        {/* Main Content */}
        {!isLoading && (
          <div className="rounded-3xl border border-emerald-100/70 bg-white/90 p-6 shadow-xl shadow-emerald-500/10 dark:border-emerald-900/50 dark:bg-zinc-950/90">
            <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
              <div>
                <h2 className="text-xl font-semibold text-zinc-900 dark:text-white">{t.email.connections}</h2>
                <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
                  {connections?.length ? `${connections.length} ${t.email.connectionsCount}` : t.email.noConnections}
                </p>
              </div>
            </div>

            {/* Connections Grid */}
            {connections && connections.length > 0 ? (
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                {connections.map((connection) => (
                  <ConnectionCard
                    key={connection.id}
                    connection={connection}
                    onEdit={() => setEditingConnection(connection)}
                    onPatterns={() => setPatternsConnection(connection)}
                    onMessages={() => setMessagesConnection(connection)}
                    onToggleEnabled={() => handleToggleEnabled(connection)}
                    onToggleAutoReply={() => handleToggleAutoReply(connection)}
                    onDelete={() => setDeleteTarget(connection)}
                  />
                ))}
              </div>
            ) : (
              <EmptyState onCreateClick={() => setShowCreateModal(true)} />
            )}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal - Same style as AgentsPage */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setDeleteTarget(null)} />
          <div className="absolute inset-x-0 top-1/2 mx-auto w-full max-w-lg -translate-y-1/2 overflow-hidden rounded-3xl border border-red-200/70 bg-white/95 shadow-2xl shadow-red-300/30 dark:border-red-900/60 dark:bg-zinc-950/95">
            <div className="flex items-center justify-between border-b border-red-200/70 bg-red-50/60 px-6 py-4 dark:border-red-900/60 dark:bg-red-950/40">
              <div>
                <h2 className="text-lg font-semibold text-red-700 dark:text-red-200">{t.email.delete.title}</h2>
                <p className="text-sm text-red-600/80 dark:text-red-300/80">{t.email.delete.irreversible}</p>
              </div>
              <button
                onClick={() => setDeleteTarget(null)}
                className="rounded-full border border-red-200/70 px-3 py-1 text-sm text-red-600 transition hover:bg-red-100/60 dark:border-red-800 dark:text-red-200 dark:hover:bg-red-900/60"
              >
                {t.email.delete.close}
              </button>
            </div>
            <div className="space-y-4 px-6 py-6 text-sm text-zinc-700 dark:text-zinc-200">
              <p>
                {t.email.delete.confirm}{' '}
                <span className="font-semibold text-zinc-900 dark:text-white">{deleteTarget.email_address}</span> ?
              </p>
              <div className="rounded-2xl border border-red-200/70 bg-red-50/70 px-4 py-3 text-sm text-red-700 dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-200">
                {t.email.delete.warning}
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 border-t border-red-200/60 bg-red-50/50 px-6 py-4 dark:border-red-900/50 dark:bg-red-950/30">
              <button
                onClick={() => setDeleteTarget(null)}
                disabled={deletePending}
                className="rounded-full border border-zinc-200/70 px-4 py-2 text-sm font-semibold text-zinc-600 transition hover:bg-zinc-100/70 dark:border-zinc-700 dark:text-zinc-200 dark:hover:bg-zinc-800/60"
              >
                {t.email.delete.cancel}
              </button>
              <button
                onClick={confirmDelete}
                disabled={deletePending}
                className="rounded-full border border-red-200/70 bg-red-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-red-600 disabled:opacity-50 dark:border-red-900/60"
              >
                {deletePending ? t.email.delete.deleting : t.email.delete.deletePermanently}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      {showCreateModal && (
        <EmailConnectionEditorModal
          preselectedAgentId={preselectedAgentId}
          onClose={() => {
            setShowCreateModal(false)
            setPreselectedAgentId(null)
          }}
        />
      )}
      
      {editingConnection && (
        <EmailConnectionEditorModal
          connection={editingConnection}
          onClose={() => setEditingConnection(null)}
        />
      )}
      
      {patternsConnection && (
        <EmailPatternsModal
          connection={patternsConnection}
          onClose={() => setPatternsConnection(null)}
        />
      )}
      
      {messagesConnection && (
        <EmailMessagesModal
          connection={messagesConnection}
          onClose={() => setMessagesConnection(null)}
        />
      )}
    </div>
  )
}

interface ConnectionCardProps {
  connection: EmailConnection
  onEdit: () => void
  onPatterns: () => void
  onMessages: () => void
  onToggleEnabled: () => void
  onToggleAutoReply: () => void
  onDelete: () => void
}

function ConnectionCard({
  connection,
  onEdit,
  onPatterns,
  onMessages,
  onToggleEnabled,
  onToggleAutoReply,
  onDelete,
}: ConnectionCardProps) {
  const { t, language } = useAppLanguage()
  const { data: totemiumStats } = useEmailStats(connection.id)
  const statusColor = connection.enabled 
    ? connection.last_error 
      ? 'text-amber-500' 
      : 'text-emerald-500'
    : 'text-zinc-400'

  const StatusIcon = connection.enabled
    ? connection.last_error
      ? AlertTriangle
      : CheckCircle
    : XCircle

  // Get provider label from translations
  const getProviderLabel = (provider: string) => {
    switch (provider) {
      case 'gmail': return t.email.providers.gmail
      case 'outlook': return t.email.providers.outlook
      case 'imap': return t.email.providers.imap
      default: return provider
    }
  }

  // Format date with locale
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr)
    const locale = language === 'fr' ? 'fr-FR' : language === 'es' ? 'es-ES' : 'en-US'
    return date.toLocaleString(locale)
  }

  return (
    <div className="rounded-2xl border border-emerald-100/60 bg-white/80 p-5 shadow-lg shadow-emerald-500/5 transition hover:shadow-xl dark:border-emerald-900/40 dark:bg-zinc-900/80">
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-100 dark:bg-emerald-900/40">
            <Mail className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
          </div>
          <div>
            <h3 className="font-semibold text-zinc-900 dark:text-white truncate max-w-[180px]">
              {connection.display_name || connection.email_address}
            </h3>
            <p className="text-xs text-zinc-500 dark:text-zinc-400">
              {getProviderLabel(connection.provider)}
            </p>
          </div>
        </div>
        <StatusIcon className={`h-5 w-5 ${statusColor}`} />
      </div>

      {/* Email Address */}
      <p className="text-sm text-zinc-600 dark:text-zinc-300 mb-4 truncate">
        {connection.email_address}
      </p>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-2 mb-3 text-sm">
        <div className="rounded-xl bg-emerald-50/80 px-3 py-2 dark:bg-emerald-900/20">
          <span className="text-xs text-zinc-500 dark:text-zinc-400">{t.email.card.processed}</span>
          <p className="font-semibold text-zinc-900 dark:text-white">
            {connection.emails_processed}
          </p>
        </div>
        <div className="rounded-xl bg-emerald-50/80 px-3 py-2 dark:bg-emerald-900/20">
          <span className="text-xs text-zinc-500 dark:text-zinc-400">{t.email.card.interval}</span>
          <p className="font-semibold text-zinc-900 dark:text-white">
            {connection.check_interval_minutes} min
          </p>
        </div>
      </div>

      {totemiumStats && (
        <div className="mb-4 rounded-xl border border-zinc-200/70 bg-zinc-50/80 px-3 py-2 text-xs dark:border-zinc-700 dark:bg-zinc-800/40">
          <p className="mb-1 font-medium text-zinc-600 dark:text-zinc-300">{t.email.card.totemiumActivity}</p>
          <p className="font-mono text-zinc-800 dark:text-zinc-100">
            {totemiumStats.emails_received} {t.email.card.statReceivedShort} · {totemiumStats.emails_replied}{' '}
            {t.email.card.statRepliedShort}
            {totemiumStats.emails_pending > 0
              ? ` · ${totemiumStats.emails_pending} ${t.email.card.statPendingShort}`
              : ''}
          </p>
        </div>
      )}

      {/* Last Check */}
      {connection.last_check_at && (
        <div className="flex items-center gap-1 text-xs text-zinc-500 dark:text-zinc-400 mb-3">
          <Clock className="h-3 w-3" />
          {t.email.card.lastCheck}: {formatDate(connection.last_check_at)}
        </div>
      )}

      {/* Error */}
      {connection.last_error && (
        <div className="mb-3 rounded-lg border border-amber-200/70 bg-amber-50/70 px-3 py-2 text-xs text-amber-700 dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-300 truncate">
          ⚠️ {connection.last_error}
        </div>
      )}

      {/* Toggles */}
      <div className="flex gap-2 mb-4">
        <button
          onClick={onToggleEnabled}
          className={`flex-1 rounded-full py-1.5 px-3 text-sm font-medium transition-colors ${
            connection.enabled
              ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
              : 'bg-zinc-100 text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400'
          }`}
        >
          {connection.enabled ? t.email.card.enabled : t.email.card.disabled}
        </button>
        <button
          onClick={onToggleAutoReply}
          className={`flex-1 rounded-full py-1.5 px-3 text-sm font-medium transition-colors ${
            connection.auto_reply_enabled
              ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
              : 'bg-zinc-100 text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400'
          }`}
        >
          {connection.auto_reply_enabled ? t.email.card.auto : t.email.card.manual}
        </button>
      </div>

      {/* Actions */}
      <div className="flex gap-1 pt-3 border-t border-emerald-100/60 dark:border-emerald-900/40">
        <button
          onClick={onMessages}
          className="flex-1 flex items-center justify-center gap-1 rounded-lg py-2 text-sm text-zinc-600 dark:text-zinc-300 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 transition"
          title={t.email.card.messages}
        >
          <MailCheck className="h-4 w-4" />
          <span className="hidden sm:inline">{t.email.card.messages}</span>
        </button>
        <button
          onClick={onPatterns}
          className="flex-1 flex items-center justify-center gap-1 rounded-lg py-2 text-sm text-zinc-600 dark:text-zinc-300 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 transition"
          title={t.email.card.patterns}
        >
          <Filter className="h-4 w-4" />
          <span className="hidden sm:inline">{t.email.card.patterns}</span>
        </button>
        <button
          onClick={onEdit}
          className="flex-1 flex items-center justify-center gap-1 rounded-lg py-2 text-sm text-zinc-600 dark:text-zinc-300 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 transition"
          title={t.email.card.settings}
        >
          <Settings className="h-4 w-4" />
        </button>
        <button
          onClick={onDelete}
          className="rounded-lg p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition"
          title={t.email.card.delete}
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </div>
  )
}

function EmptyState({ onCreateClick }: { onCreateClick: () => void }) {
  const { t } = useAppLanguage()
  return (
    <div className="text-center py-16 rounded-2xl border-2 border-dashed border-emerald-200/70 bg-emerald-50/30 dark:border-emerald-900/50 dark:bg-emerald-950/20">
      <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-emerald-100 dark:bg-emerald-900/40">
        <Mail className="h-8 w-8 text-emerald-600 dark:text-emerald-400" />
      </div>
      <h3 className="text-lg font-semibold text-zinc-900 dark:text-white mb-2">
        {t.email.empty.title}
      </h3>
      <p className="text-zinc-500 dark:text-zinc-400 mb-6 max-w-md mx-auto">
        {t.email.empty.description}
      </p>
      <button
        onClick={onCreateClick}
        className="inline-flex items-center gap-2 rounded-full border border-emerald-200/70 bg-emerald-500 px-6 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-600 dark:border-emerald-700"
      >
        <Plus className="h-5 w-5" />
        {t.email.empty.addConnection}
      </button>
    </div>
  )
}
